/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'

const TITLE_LINE_HEIGHT = 20

const cardCSS = css`
  display: flex;
  flex-direction: column;
  min-width: 240px;
  border-radius: 8px;
  color: rgba(0, 0, 0, 0.5);
  &:hover {
    background: rgba(242, 236, 228, 0.5);
    color: rgba(0, 0, 0, 0.9);
  }
`

const contentRowCSS = css`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
`

const iconCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: ${TITLE_LINE_HEIGHT}px;
`

const textColumnCSS = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const imageCSS = css`
  display: block;
  width: 100%;
  margin-top: -4px;
  border-radius: 8px;
`

type Props = {
  icon?: React.ReactNode
  title: React.ReactNode
  subtitle?: string
  url: string
  // Showcase image rendered under the text, inside the same link — fades out
  // at the bottom. Used by feature columns (e.g. the ebook in Resources).
  image?: { src: string; alt: string }
  // Fires when the cursor enters the card — used by the Solutions menu to
  // swap the right-side quote panel to the matching customer quote.
  onPointerEnter?: () => void
  // Fires when the card is clicked — used to close the dropdown.
  onClick?: () => void
}

export const NavMenuCard = ({
  icon,
  title,
  subtitle,
  url,
  image,
  onPointerEnter,
  onClick,
}: Props) => {
  return (
    <a
      href={url}
      className="padding-horizontal-16 padding-vertical-8"
      css={cardCSS}
      onPointerEnter={onPointerEnter}
      onClick={onClick}
    >
      <span css={contentRowCSS}>
        {icon && <span css={iconCSS}>{icon}</span>}
        <span css={textColumnCSS}>
          <span
            className="font-family-system font-weight-medium shade-05"
            style={{
              fontSize: 15,
              fontWeight: 600,
              lineHeight: `${TITLE_LINE_HEIGHT}px`,
            }}
          >
            {title}
          </span>
          {!!subtitle && (
            <span
              className="font-family-system shade-30"
              style={{
                fontSize: 13,
                fontWeight: 400,
                marginTop: 0,
                color: 'rgba(0,0,0,0.5)',
              }}
            >
              {subtitle}
            </span>
          )}
        </span>
      </span>
      {image && <img css={imageCSS} src={image.src} alt={image.alt} />}
    </a>
  )
}
